import React, { memo, useCallback } from 'react';
import { List } from 'tea-component';
import moment from 'moment';
import classNames from 'classnames';
import { isEmpty } from 'lodash-es';
import { CustomDropdown } from '../../../ChatDSTemplate/components';
import EllipsisHover from '@gp/components/EllipsisHover';
import BasicImage from '@home/components/BasicImage';
import DefauletFile from '@/pages/aigc/assets/svg/agent-ds-temp-file.svg';
import defaultFileIcon from '@/pages/aigc/assets/svg/excel.svg';
import './index.scss';

/**
 * 文件项接口
 */
export interface IFileItem {
  /** 文件 ID */
  id: string;
  /** 文件名 */
  name: string;
  /** COS 文件地址 */
  cos_url?: string;
  /** 创建时间 */
  create_time?: string;
  /** 更新时间 */
  update_time?: string;
  /** 原始文件信息（用于重置功能） */
  original_file?: {
    id: string;
    name: string;
    cos_url: string;
  };
}

interface IFileSelectorProps {
  /** 文件列表 */
  fileList: IFileItem[];
  /** 当前选中的文件（受控） */
  currentFile?: IFileItem;
  /** 文件变化回调 */
  onChange?: (file: IFileItem) => void;
  /** 默认显示文案 */
  placeholder?: string;
  /** 自定义类名 */
  className?: string;
  /** 是否禁用 */
  disabled?: boolean;
}

/**
 * 文件选择器组件（纯 UI 组件）
 * 使用 CustomDropdown 实现下拉选择文件
 * 状态完全由父级控制
 */
// eslint-disable-next-line react/display-name
const FileSelector = memo(({
  fileList,
  currentFile,
  onChange,
  placeholder,
  className,
  disabled = false,
}: IFileSelectorProps) => {
  // 文件列表为空时不展示下拉，纯展示当前文件信息（非禁用态，仅无下拉交互）
  const isListEmpty = isEmpty(fileList);

  // 文件变化处理
  const handleFileChange = useCallback((file: IFileItem) => {
    if (currentFile?.id === file.id) return;
    onChange?.(file);
  }, [currentFile?.id, onChange]);

  // 文件下拉菜单
  const renderMenu = useCallback(
    (close: () => void) => (
      <List type="option">
        {fileList.map((item, index) => (
          <List.Item
            key={item.id + index}
            selected={currentFile?.id === item.id}
            onClick={() => {
              handleFileChange(item);
              close();
            }}
          >
            <div className="dba-file-selector__menu-item">
              <div className="dba-file-selector__menu-item-icon">
                <BasicImage src={defaultFileIcon} errorSrc={defaultFileIcon} />
              </div>
              <div className="dba-file-selector__menu-item-content">
                <EllipsisHover text={item.name} placement={'right'}/>
                {item.update_time && (
                  <span className="dba-file-selector__menu-item-date">
                    {moment(item.update_time).format('YYYY-MM-DD HH:mm')}
                  </span>
                )}
              </div>
            </div>
          </List.Item>
        ))}
      </List>
    ),
    [fileList, currentFile, handleFileChange],
  );

  // 文件选择按钮内容：文件列表为空时纯展示（无下拉交互），否则包一层 CustomDropdown
  const renderButton = (
    <>
      <img src={DefauletFile} alt="" className="dba-file-selector__icon" />
      <div className="dba-file-selector__title">
        <EllipsisHover text={currentFile?.name || placeholder || '-'} placement={'right'} />
      </div>
    </>
  );

  return (
    <div className={classNames('dba-file-selector', className, { 'dba-file-selector--disabled': disabled })}>
      {isListEmpty ? (
        renderButton
      ) : (
        <CustomDropdown
          menu={renderMenu}
          overlayClassName="dba-file-selector-overlay"
          disabled={disabled}
          button={renderButton}
        />
      )}
    </div>
  );
});

export default FileSelector;
